import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, writeBatch, serverTimestamp, doc, getDocs, query } from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { useLocation, Link } from 'react-router-dom';
import {
    FaPrint,
    FaSpinner,
    FaUpload,
    FaTh,
    FaLayerGroup,
    FaArrowLeft,
    FaSearch,
    FaInfoCircle,
    FaCut,
    FaEye
} from 'react-icons/fa';
import QRCode from 'qrcode';

const CM_TO_PX = 37.795;
const LABEL_H = 0.5; // cm – strip below sticker for the application code

// Paper size dimensions in CM [width, height] (portrait)
const PAPER_SIZES = {
    A4: { label: 'A4 (21.0 x 29.7 cm) - Standart Tabaka', width: 21.0, height: 29.7 },
    A3: { label: 'A3 (29.7 x 42.0 cm) - Büyük Boy Tabaka', width: 29.7, height: 42.0 },
    A5: { label: 'A5 (14.8 x 21.0 cm) - Yarım Tabaka', width: 14.8, height: 21.0 },
    Letter: { label: 'US Letter (21.59 x 27.94 cm)', width: 21.59, height: 27.94 },
    Single: { label: 'Tekil Sticker (Her Sayfada 1 Adet)', width: 0, height: 0 }
};

// Shared path builder for rounded rectangles
function roundedRectPath(ctx, x, y, w, h, r) {
    r = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}

// Draw one of the 3 QR finder patterns as smooth nested rounded rectangles
function drawFinderPattern(ctx, startX, startY, cellPx, cornerRadiusPct, darkColor) {
    const pct = cornerRadiusPct / 100;
    const s7 = 7 * cellPx;
    const s5 = 5 * cellPx;
    const s3 = 3 * cellPx;
    const r7 = Math.min(s7 * pct, s7 / 2);
    const r5 = Math.min(s5 * pct, s5 / 2);
    const r3 = Math.min(s3 * pct, s3 / 2);

    ctx.fillStyle = darkColor;
    ctx.beginPath();
    roundedRectPath(ctx, startX, startY, s7, s7, r7);
    ctx.fill();

    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    roundedRectPath(ctx, startX + cellPx, startY + cellPx, s5, s5, r5);
    ctx.fill();

    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = darkColor;
    ctx.beginPath();
    roundedRectPath(ctx, startX + 2 * cellPx, startY + 2 * cellPx, s3, s3, r3);
    ctx.fill();
}

// Custom QR canvas renderer
function drawQROnCanvas(canvas, text, pixelSize, {
    transparent = false,
    cornerRadiusPct = 0,
    outerRadiusPct = 0,
    darkColor = '#000000',
    margin = 1,
} = {}) {
    const qr = QRCode.create(text, { errorCorrectionLevel: 'M' });
    const modules = qr.modules;
    const n = modules.size;

    // Adaptive margin to ensure rounded corners don't cut off corner finder patterns
    const effectiveMargin = outerRadiusPct > 0
        ? Math.max(margin, 1.4 + (outerRadiusPct / 50) * 1.6)
        : Math.max(margin, 1.2);

    const totalCells = n + effectiveMargin * 2;
    const cellPx = pixelSize / totalCells;

    canvas.width = pixelSize;
    canvas.height = pixelSize;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, pixelSize, pixelSize);

    const outerR = outerRadiusPct > 0
        ? Math.min((pixelSize / 2) * (outerRadiusPct / 50), pixelSize / 2)
        : 0;

    // Apply clipping path if outer radius is enabled
    ctx.save();
    if (outerR > 0) {
        ctx.beginPath();
        roundedRectPath(ctx, 0, 0, pixelSize, pixelSize, outerR);
        ctx.clip();
    }

    if (!transparent) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pixelSize, pixelSize);
    }

    ctx.fillStyle = darkColor;

    for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
            if (!modules.get(row, col)) continue;
            const inTL = row < 7 && col < 7;
            const inTR = row < 7 && col >= n - 7;
            const inBL = row >= n - 7 && col < 7;
            if (inTL || inTR || inBL) continue;

            const x0 = (col + effectiveMargin) * cellPx;
            const y0 = (row + effectiveMargin) * cellPx;
            const modR = cornerRadiusPct > 0 ? (cellPx / 2) * (cornerRadiusPct / 50) : 0;

            if (modR > 0) {
                ctx.beginPath();
                roundedRectPath(ctx, x0, y0, cellPx, cellPx, modR);
                ctx.fill();
            } else {
                ctx.fillRect(x0, y0, cellPx, cellPx);
            }
        }
    }

    const tlX = effectiveMargin * cellPx;
    const tlY = effectiveMargin * cellPx;
    const trX = (effectiveMargin + n - 7) * cellPx;
    const trY = effectiveMargin * cellPx;
    const blX = effectiveMargin * cellPx;
    const blY = (effectiveMargin + n - 7) * cellPx;

    drawFinderPattern(ctx, tlX, tlY, cellPx, cornerRadiusPct, darkColor);
    drawFinderPattern(ctx, trX, trY, cellPx, cornerRadiusPct, darkColor);
    drawFinderPattern(ctx, blX, blY, cellPx, cornerRadiusPct, darkColor);

    ctx.restore();
}

// Draw cutting guides (crop marks or dashed border) on jsPDF
function drawCutGuidesOnPDF(pdf, posX, posY, w, h, style, colorMode, gapX, gapY, isFirstCol, isLastCol, isFirstRow, isLastRow, pageMargin) {
    if (colorMode === 'red') {
        pdf.setDrawColor(220, 38, 38);
    } else if (colorMode === 'light') {
        pdf.setDrawColor(200, 200, 200);
    } else {
        pdf.setDrawColor(75, 85, 99);
    }
    pdf.setLineWidth(0.012);

    const baseMark = 0.25;
    const leftLen = isFirstCol ? Math.min(baseMark, pageMargin * 0.8) : (gapX > 0 ? Math.min(baseMark, (gapX / 2) * 0.85) : 0);
    const rightLen = isLastCol ? Math.min(baseMark, pageMargin * 0.8) : (gapX > 0 ? Math.min(baseMark, (gapX / 2) * 0.85) : 0);
    const topLen = isFirstRow ? Math.min(baseMark, pageMargin * 0.8) : (gapY > 0 ? Math.min(baseMark, (gapY / 2) * 0.85) : 0);
    const botLen = isLastRow ? Math.min(baseMark, pageMargin * 0.8) : (gapY > 0 ? Math.min(baseMark, (gapY / 2) * 0.85) : 0);

    if (style === 'dashed_border' || style === 'both') {
        pdf.setLineDashPattern([0.15, 0.12], 0);
        pdf.rect(posX, posY, w, h);
        pdf.setLineDashPattern([], 0);
    }

    if (style === 'crop_marks' || style === 'both') {
        pdf.setLineDashPattern([], 0);

        // Top-Left corner
        if (leftLen > 0) pdf.line(posX - leftLen, posY, posX, posY);
        if (topLen > 0) pdf.line(posX, posY - topLen, posX, posY);

        // Top-Right corner
        if (rightLen > 0) pdf.line(posX + w, posY, posX + w + rightLen, posY);
        if (topLen > 0) pdf.line(posX + w, posY - topLen, posX + w, posY);

        // Bottom-Left corner
        if (leftLen > 0) pdf.line(posX - leftLen, posY + h, posX, posY + h);
        if (botLen > 0) pdf.line(posX, posY + h, posX, posY + h + botLen);

        // Bottom-Right corner
        if (rightLen > 0) pdf.line(posX + w, posY + h, posX + w + rightLen, posY + h);
        if (botLen > 0) pdf.line(posX + w, posY + h, posX + w, posY + h + botLen);
    }
}

// ─── PDF Generation with Sheet Grid Packing ─────────────────────────────────
const generateGridPDF = async (codes, config, onProgress) => {
    const { jsPDF } = await import('jspdf');
    const html2canvas = (await import('html2canvas')).default;

    const {
        paperSize,
        orientation,
        stickerW,
        stickerH,
        pageMargin,
        gapX,
        gapY,
        qrX,
        qrY,
        qrSize,
        stickerImageUrl,
        backsideImageUrl,
        qrTransparent,
        qrCornerRadius,
        qrOuterRadius,
        showCutGuides,
        cutGuideStyle = 'crop_marks',
        cutGuideColor = 'dark',
        showLabelCode
    } = config;

    const isSingle = paperSize === 'Single';

    // Determine Page Width and Height in CM
    let pageW, pageH;
    if (isSingle) {
        pageW = stickerW;
        pageH = stickerH + (showLabelCode ? LABEL_H : 0);
    } else {
        const spec = PAPER_SIZES[paperSize] || PAPER_SIZES.A4;
        if (orientation === 'landscape') {
            pageW = Math.max(spec.width, spec.height);
            pageH = Math.min(spec.width, spec.height);
        } else {
            pageW = Math.min(spec.width, spec.height);
            pageH = Math.max(spec.width, spec.height);
        }
    }

    // Grid layout calculation
    const usableW = Math.max(0.1, pageW - 2 * pageMargin);
    const usableH = Math.max(0.1, pageH - 2 * pageMargin);

    const cols = isSingle ? 1 : Math.max(1, Math.floor((usableW + gapX) / (stickerW + gapX)));
    const rows = isSingle ? 1 : Math.max(1, Math.floor((usableH + gapY) / (stickerH + gapY)));
    const stickersPerPage = cols * rows;

    // Centering offsets
    const totalGridW = cols * stickerW + (cols - 1) * gapX;
    const totalGridH = rows * stickerH + (rows - 1) * gapY;
    const startX = isSingle ? 0 : Math.max(pageMargin, (pageW - totalGridW) / 2);
    const startY = isSingle ? 0 : Math.max(pageMargin, (pageH - totalGridH) / 2);

    const pdf = new jsPDF({
        orientation: isSingle ? (stickerW > pageH ? 'landscape' : 'portrait') : orientation,
        unit: 'cm',
        format: isSingle ? [pageW, pageH] : [pageW, pageH]
    });

    // Hidden container for sticker rendering
    const container = document.createElement('div');
    container.style.cssText = `position:fixed;top:-9999px;left:-9999px;width:${stickerW * CM_TO_PX}px;height:${stickerH * CM_TO_PX}px;`;
    document.body.appendChild(container);

    const renderStickerCanvas = async (imageUrl, qrOpts = null) => {
        const el = document.createElement('div');
        el.style.cssText = `width:${stickerW * CM_TO_PX}px;height:${stickerH * CM_TO_PX}px;position:relative;overflow:hidden;background:#fff;`;

        if (imageUrl) {
            const bg = document.createElement('img');
            bg.src = imageUrl;
            bg.crossOrigin = 'anonymous';
            bg.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;object-fit:fill;';
            el.appendChild(bg);
            await new Promise(r => { bg.onload = r; bg.onerror = r; });
        }

        if (qrOpts) {
            const { url, x, y, size, transparent, cornerRadius, outerRadius } = qrOpts;
            const qrPx = Math.round(size * CM_TO_PX);
            const HighResMult = 4;
            const outerRPx = outerRadius > 0 ? Math.round((qrPx / 2) * (outerRadius / 50)) : 0;

            const qrCanvas = document.createElement('canvas');
            drawQROnCanvas(qrCanvas, url, qrPx * HighResMult, {
                transparent,
                cornerRadiusPct: cornerRadius,
                outerRadiusPct: outerRadius,
                darkColor: '#000000',
            });

            const qrWrapper = document.createElement('div');
            qrWrapper.style.cssText = `position:absolute;left:${x * CM_TO_PX}px;top:${y * CM_TO_PX}px;width:${qrPx}px;height:${qrPx}px;overflow:hidden;border-radius:${outerRPx}px;${transparent ? 'background:transparent;' : 'background:#ffffff;'}`;

            qrCanvas.style.cssText = `width:100%;height:100%;display:block;border-radius:${outerRPx}px;`;
            qrWrapper.appendChild(qrCanvas);
            el.appendChild(qrWrapper);
        }

        container.innerHTML = '';
        container.appendChild(el);
        await new Promise(r => setTimeout(r, 40));

        return html2canvas(el, {
            scale: 4,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: stickerW * CM_TO_PX,
            height: stickerH * CM_TO_PX,
        });
    };

    let backCanvas = null;
    if (backsideImageUrl) {
        backCanvas = await renderStickerCanvas(backsideImageUrl);
    }

    // Process all codes and pack into sheets
    for (let i = 0; i < codes.length; i++) {
        const code = codes[i];
        const slotInPage = i % stickersPerPage;

        // When starting a new sheet (except very first page)
        if (i > 0 && slotInPage === 0) {
            pdf.addPage([pageW, pageH], orientation);
        }

        // Calculate (col, row) coordinates
        const c = slotInPage % cols;
        const r = Math.floor(slotInPage / cols);
        const posX = startX + c * (stickerW + gapX);
        const posY = startY + r * (stickerH + gapY);

        // Render front sticker with its unique QR code
        const frontCanvas = await renderStickerCanvas(stickerImageUrl, {
            url: code.url,
            x: qrX, y: qrY, size: qrSize,
            transparent: qrTransparent,
            cornerRadius: qrCornerRadius,
            outerRadius: qrOuterRadius,
        });

        // Place image on the sheet
        pdf.addImage(frontCanvas.toDataURL('image/png'), 'PNG', posX, posY, stickerW, stickerH);

        // Draw cutting guides (crop marks / dashed border) if requested
        if (showCutGuides && !isSingle) {
            const isFirstCol = c === 0;
            const isLastCol = c === cols - 1;
            const isFirstRow = r === 0;
            const isLastRow = r === rows - 1;
            drawCutGuidesOnPDF(pdf, posX, posY, stickerW, stickerH, cutGuideStyle, cutGuideColor, gapX, gapY, isFirstCol, isLastCol, isFirstRow, isLastRow, pageMargin);
        }

        // Print code label
        if (showLabelCode) {
            pdf.setFontSize(6.5);
            pdf.setTextColor(80, 80, 80);
            if (isSingle) {
                pdf.text(code.id, stickerW / 2, stickerH + LABEL_H / 2 + 0.05, { align: 'center' });
            } else {
                pdf.text(code.id, posX + stickerW / 2, posY + stickerH - 0.15, { align: 'center' });
            }
        }

        if (onProgress) {
            onProgress(Math.round(((i + 1) / codes.length) * 100));
        }
    }

    // If backside is uploaded and not in single mode, append backside sheets
    if (backsideImageUrl && backCanvas && !isSingle) {
        const totalSheets = Math.ceil(codes.length / stickersPerPage);
        for (let s = 0; s < totalSheets; s++) {
            pdf.addPage([pageW, pageH], orientation);
            const codesOnThisSheet = Math.min(stickersPerPage, codes.length - s * stickersPerPage);

            for (let slot = 0; slot < codesOnThisSheet; slot++) {
                const c = slot % cols;
                const r = Math.floor(slot / cols);
                // For double sided printing, horizontally flip columns so back aligns with front!
                const flippedCol = cols - 1 - c;
                const posX = startX + flippedCol * (stickerW + gapX);
                const posY = startY + r * (stickerH + gapY);

                pdf.addImage(backCanvas.toDataURL('image/png'), 'PNG', posX, posY, stickerW, stickerH);

                if (showCutGuides) {
                    const isFirstCol = flippedCol === 0;
                    const isLastCol = flippedCol === cols - 1;
                    const isFirstRow = r === 0;
                    const isLastRow = r === rows - 1;
                    drawCutGuidesOnPDF(pdf, posX, posY, stickerW, stickerH, cutGuideStyle, cutGuideColor, gapX, gapY, isFirstCol, isLastCol, isFirstRow, isLastRow, pageMargin);
                }
            }
        }
    }

    document.body.removeChild(container);
    pdf.save(`kodutara-stickers-${codes.length}-${paperSize}.pdf`);
};

// ─── Component ───────────────────────────────────────────────────────────────
const AdminPrintQrPage = () => {
    const location = useLocation();

    // Source Mode: 'existing' (select from DB / url) or 'generate' (batch create new)
    const [sourceMode, setSourceMode] = useState('generate');

    // Batch Generation Form State
    const [prefix, setPrefix] = useState('KT');
    const [count, setCount] = useState(10);

    // Existing Codes State
    const [allDbQrs, setAllDbQrs] = useState([]);
    const [selectedCodesSet, setSelectedCodesSet] = useState(new Set());
    const [dbSearch, setDbSearch] = useState('');
    const [loadingDbQrs, setLoadingDbQrs] = useState(false);

    // Paper & Grid Configuration
    const [paperSize, setPaperSize] = useState('A4'); // 'A4', 'A3', 'A5', 'Letter', 'Single'
    const [orientation, setOrientation] = useState('portrait'); // 'portrait', 'landscape'
    const [stickerW, setStickerW] = useState(8.5);
    const [stickerH, setStickerH] = useState(5.4);
    const [pageMargin, setPageMargin] = useState(1.0);
    const [gapX, setGapX] = useState(0.4);
    const [gapY, setGapY] = useState(0.4);

    // Cut Guides & Label
    const [showCutGuides, setShowCutGuides] = useState(true);
    const [cutGuideStyle, setCutGuideStyle] = useState('crop_marks'); // 'crop_marks', 'dashed_border', 'both'
    const [cutGuideColor, setCutGuideColor] = useState('dark'); // 'dark', 'light', 'red'
    const [showLabelCode, setShowLabelCode] = useState(true);

    // QR Code Position & Styling on Sticker
    const [qrX, setQrX] = useState(0.5);
    const [qrY, setQrY] = useState(0.5);
    const [qrSize, setQrSize] = useState(3.8);
    const [qrTransparent, setQrTransparent] = useState(false);
    const [qrCornerRadius, setQrCornerRadius] = useState(0);
    const [qrOuterRadius, setQrOuterRadius] = useState(0);

    // Images
    const [stickerImageUrl, setStickerImageUrl] = useState(null);
    const [backsideImageUrl, setBacksideImageUrl] = useState(null);

    // Progress & Execution
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [progress, setProgress] = useState(0);
    const [previewSide, setPreviewSide] = useState('front');

    const qrPreviewCanvasRef = useRef(null);

    // Parse URL query parameter `?selectedCodes=KT1,KT2...`
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const codesParam = params.get('selectedCodes');
        if (codesParam) {
            const list = codesParam.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
            if (list.length > 0) {
                setSelectedCodesSet(new Set(list));
                setSourceMode('existing');
            }
        }
    }, [location.search]);

    // Fetch DB QRs when entering 'existing' mode
    const fetchExistingQrs = useCallback(async () => {
        setLoadingDbQrs(true);
        try {
            const snap = await getDocs(query(collection(db, 'qrCodes')));
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
            setAllDbQrs(list);
        } catch (e) {
            console.error('QR listesi alınırken hata:', e);
        } finally {
            setLoadingDbQrs(false);
        }
    }, []);

    useEffect(() => {
        if (sourceMode === 'existing') {
            fetchExistingQrs();
        }
    }, [sourceMode, fetchExistingQrs]);

    // Grid Layout Calculations
    const gridCalc = useMemo(() => {
        if (paperSize === 'Single') {
            return {
                pageW: stickerW,
                pageH: stickerH + (showLabelCode ? LABEL_H : 0),
                cols: 1,
                rows: 1,
                perPage: 1,
                startX: 0,
                startY: 0
            };
        }

        const spec = PAPER_SIZES[paperSize] || PAPER_SIZES.A4;
        const pageW = orientation === 'landscape' ? Math.max(spec.width, spec.height) : Math.min(spec.width, spec.height);
        const pageH = orientation === 'landscape' ? Math.min(spec.width, spec.height) : Math.max(spec.width, spec.height);

        const usableW = Math.max(0.1, pageW - 2 * pageMargin);
        const usableH = Math.max(0.1, pageH - 2 * pageMargin);

        const cols = Math.max(1, Math.floor((usableW + gapX) / (stickerW + gapX)));
        const rows = Math.max(1, Math.floor((usableH + gapY) / (stickerH + gapY)));
        const perPage = cols * rows;

        const totalGridW = cols * stickerW + (cols - 1) * gapX;
        const totalGridH = rows * stickerH + (rows - 1) * gapY;
        const startX = Math.max(pageMargin, (pageW - totalGridW) / 2);
        const startY = Math.max(pageMargin, (pageH - totalGridH) / 2);

        return { pageW, pageH, cols, rows, perPage, startX, startY };
    }, [paperSize, orientation, stickerW, stickerH, pageMargin, gapX, gapY, showLabelCode]);

    // Miniature Sheet sticker placement list
    const sheetStickers = useMemo(() => {
        const list = [];
        const count = gridCalc.perPage;
        for (let slot = 0; slot < count; slot++) {
            const c = slot % gridCalc.cols;
            const r = Math.floor(slot / gridCalc.cols);
            const x = gridCalc.startX + c * (stickerW + gapX);
            const y = gridCalc.startY + r * (stickerH + gapY);
            list.push({ idx: slot + 1, x, y, c, r });
        }
        return list;
    }, [gridCalc, stickerW, stickerH, gapX, gapY]);

    // Total Codes Count depending on mode
    const totalSelectedCount = sourceMode === 'existing' ? selectedCodesSet.size : Number(count) || 0;
    const totalSheetsEstimate = gridCalc.perPage > 0 ? Math.ceil(totalSelectedCount / gridCalc.perPage) : 1;

    // Filtered DB QRs
    const filteredDbQrs = useMemo(() => {
        if (!dbSearch.trim()) return allDbQrs;
        const q = dbSearch.toLowerCase();
        return allDbQrs.filter(code => code.id.toLowerCase().includes(q));
    }, [allDbQrs, dbSearch]);

    // Sample QR Preview setup
    const SAMPLE_CODE = `${prefix || 'KT'}SAMPLE`;
    const SAMPLE_URL = `https://kodutara.web.app/q/${SAMPLE_CODE}`;
    const qrPxSize = qrSize * CM_TO_PX;

    useEffect(() => {
        if (!qrPreviewCanvasRef.current) return;
        try {
            drawQROnCanvas(qrPreviewCanvasRef.current, SAMPLE_URL, qrPxSize, {
                transparent: qrTransparent,
                cornerRadiusPct: qrCornerRadius,
                outerRadiusPct: qrOuterRadius,
                darkColor: '#000000',
            });
        } catch (_) { }
    }, [SAMPLE_URL, qrPxSize, qrTransparent, qrCornerRadius, qrOuterRadius]);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setStickerImageUrl(URL.createObjectURL(file));
    };

    const handleBacksideUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setBacksideImageUrl(URL.createObjectURL(file));
    };

    // Toggle single code selection in DB table
    const toggleCodeSelect = (id) => {
        const next = new Set(selectedCodesSet);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedCodesSet(next);
    };

    // Quick select first N codes from search list
    const selectFirstN = (n) => {
        const next = new Set(selectedCodesSet);
        filteredDbQrs.slice(0, n).forEach(q => next.add(q.id));
        setSelectedCodesSet(next);
    };

    // Submit handler
    const handleGenerateAndPrint = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        let codesToPrint = [];

        if (sourceMode === 'existing') {
            if (selectedCodesSet.size === 0) {
                return setError('Lütfen listeden en az 1 QR kodu işaretleyin.');
            }
            codesToPrint = Array.from(selectedCodesSet).map(id => ({
                id,
                url: `https://kodutara.web.app/q/${id}`
            }));
        } else {
            const num = Number(count);
            if (!Number.isInteger(num) || num <= 0 || num > 2000) {
                return setError('Lütfen 1 ile 2000 arasında geçerli bir adet girin.');
            }

            setLoading(true);
            setProgress(5);

            try {
                const codesCollectionRef = collection(db, 'qrCodes');
                const BATCH_SIZE = 450;

                for (let batchStart = 0; batchStart < num; batchStart += BATCH_SIZE) {
                    const batch = writeBatch(db);
                    const batchEnd = Math.min(batchStart + BATCH_SIZE, num);
                    for (let i = batchStart; i < batchEnd; i++) {
                        const newCode = `${prefix.toUpperCase()}-${nanoid(6).toUpperCase()}`;
                        const newCodeRef = doc(codesCollectionRef, newCode);
                        batch.set(newCodeRef, {
                            code: newCode,
                            isLinked: false,
                            linkedUserId: null,
                            createdAt: serverTimestamp(),
                            linkedAt: null,
                            prefix: prefix.toUpperCase()
                        });
                        codesToPrint.push({ id: newCode, url: `https://kodutara.web.app/q/${newCode}` });
                    }
                    await batch.commit();
                    setProgress(Math.round((codesToPrint.length / num) * 30));
                }
            } catch (err) {
                console.error('Batch QR oluşturma hatası:', err);
                setLoading(false);
                return setError(`Kodlar oluşturulurken hata: ${err.message}`);
            }
        }

        setLoading(true);
        setSuccess(`${codesToPrint.length} adet QR kodu için ${paperSize} grid PDF hazırlanıyor...`);

        try {
            await generateGridPDF(
                codesToPrint,
                {
                    paperSize,
                    orientation,
                    stickerW,
                    stickerH,
                    pageMargin,
                    gapX,
                    gapY,
                    qrX,
                    qrY,
                    qrSize,
                    stickerImageUrl,
                    backsideImageUrl,
                    qrTransparent,
                    qrCornerRadius,
                    qrOuterRadius,
                    showCutGuides,
                    cutGuideStyle,
                    cutGuideColor,
                    showLabelCode
                },
                (pct) => setProgress(30 + Math.round(pct * 0.7))
            );

            setProgress(100);
            setSuccess(`Tebrikler! ${codesToPrint.length} sticker içeren PDF başarıyla oluşturuldu (${paperSize} - ${totalSheetsEstimate} sayfa)!`);
        } catch (err) {
            console.error('PDF oluşturma hatası:', err);
            setError(`PDF oluşturulurken hata: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Cut guide color helper for SVG preview
    const svgCutColor = cutGuideColor === 'red' ? '#dc2626' : cutGuideColor === 'light' ? '#cbd5e1' : '#475569';
    const previewScale = 28;
    const previewOuterRPx = qrOuterRadius > 0 ? (qrSize * previewScale / 2) * (qrOuterRadius / 50) : 0;

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                    <div className="flex items-center space-x-3">
                        <Link
                            to="/admin"
                            className="p-2.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-black transition-colors"
                            title="Admin Paneline Dön"
                        >
                            <FaArrowLeft size={16} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                                <FaPrint className="text-amber-500" />
                                <span>Çoklu Sticker & Sayfa Grid PDF Baskı</span>
                            </h1>
                            <p className="text-xs text-gray-500 mt-0.5">
                                A4/A3 tabaka boyutu seçin, sayfaya sığacak ızgarayı otomatik hesaplayın ve profesyonel kesim kılavuzlarıyla PDF indirin.
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <Link
                            to="/admin/qrcodes"
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition-colors"
                        >
                            Tüm QR Kodları İncele
                        </Link>
                    </div>
                </div>

                {/* Source Selection Tabs: New Batch vs Existing Checkbox Selection */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={() => setSourceMode('generate')}
                            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                sourceMode === 'generate'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <FaTh />
                            <span>1. Yeni QR Kodları Üret & Tabakaya Bas</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setSourceMode('existing')}
                            className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
                                sourceMode === 'existing'
                                    ? 'bg-blue-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            <FaLayerGroup />
                            <span>2. Mevcut QR Kodlarından Seç & Bas ({selectedCodesSet.size})</span>
                        </button>
                    </div>

                    {sourceMode === 'existing' && selectedCodesSet.size > 0 && (
                        <div className="text-xs font-bold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200">
                            {selectedCodesSet.size} adet QR kodu baskı için işaretlendi
                        </div>
                    )}
                </div>

                {/* Main Grid: Form on Left, Live Previews on Right */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

                    {/* Left Form: 7 cols */}
                    <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
                        <form onSubmit={handleGenerateAndPrint} className="space-y-6">

                            {/* Existing Codes Selection Drawer (When in 'existing' mode) */}
                            {sourceMode === 'existing' && (
                                <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-bold text-gray-800">
                                            Baskı Alınacak QR Kodlarını İşaretleyin:
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => selectFirstN(10)}
                                                className="text-[11px] font-semibold text-blue-600 hover:underline"
                                            >
                                                İlk 10'u Seç
                                            </button>
                                            <span className="text-gray-300">|</span>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedCodesSet(new Set())}
                                                className="text-[11px] font-semibold text-red-600 hover:underline"
                                            >
                                                Temizle
                                            </button>
                                        </div>
                                    </div>

                                    {/* Search input for DB QRs */}
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                                        <input
                                            type="text"
                                            value={dbSearch}
                                            onChange={(e) => setDbSearch(e.target.value)}
                                            placeholder="QR Kodu ara..."
                                            className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-300 bg-white"
                                        />
                                    </div>

                                    {/* Scrollable Mini Table */}
                                    <div className="max-h-48 overflow-y-auto border rounded-lg bg-white divide-y divide-gray-100 text-xs">
                                        {loadingDbQrs ? (
                                            <div className="p-4 text-center text-gray-400">
                                                <FaSpinner className="animate-spin inline mr-2" /> QR listesi yükleniyor...
                                            </div>
                                        ) : filteredDbQrs.length === 0 ? (
                                            <div className="p-4 text-center text-gray-400">QR bulunamadı</div>
                                        ) : (
                                            filteredDbQrs.map(qr => {
                                                const isChecked = selectedCodesSet.has(qr.id);
                                                return (
                                                    <label
                                                        key={qr.id}
                                                        className={`flex items-center justify-between p-2 cursor-pointer hover:bg-blue-50/50 ${
                                                            isChecked ? 'bg-blue-50/80 font-bold' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={isChecked}
                                                                onChange={() => toggleCodeSelect(qr.id)}
                                                                className="rounded text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <span className="font-mono">{qr.id}</span>
                                                        </div>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                                                            qr.isLinked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                            {qr.isLinked ? 'Bağlı' : 'Boşta'}
                                                        </span>
                                                    </label>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Batch Generation Count & Prefix (When in 'generate' mode) */}
                            {sourceMode === 'generate' && (
                                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Kod Ön Eki</label>
                                        <input
                                            type="text"
                                            value={prefix}
                                            onChange={e => setPrefix(e.target.value.toUpperCase())}
                                            maxLength={5}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Üretilecek Adet</label>
                                        <input
                                            type="number"
                                            min="1"
                                            max="2000"
                                            value={count}
                                            onChange={e => setCount(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ── Section: Paper Size & Sheet Grid ── */}
                            <div className="p-4 bg-amber-50/60 rounded-xl border border-amber-200 space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                        <FaLayerGroup className="text-amber-600" />
                                        <span>Kağıt / Tabaka ve Izgara Boyutu</span>
                                    </h3>
                                    <span className="text-xs bg-amber-200/60 text-amber-900 px-2 py-0.5 rounded font-semibold">
                                        {gridCalc.perPage} Sticker / Sayfa
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Kağıt Boyutu</label>
                                        <select
                                            value={paperSize}
                                            onChange={(e) => setPaperSize(e.target.value)}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none font-medium"
                                        >
                                            {Object.entries(PAPER_SIZES).map(([key, spec]) => (
                                                <option key={key} value={key}>{spec.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-700 mb-1">Sayfa Yönü</label>
                                        <select
                                            value={orientation}
                                            onChange={(e) => setOrientation(e.target.value)}
                                            disabled={paperSize === 'Single'}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:opacity-50"
                                        >
                                            <option value="portrait">Dikey (Portrait)</option>
                                            <option value="landscape">Yatay (Landscape)</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Sticker Dimensions & Spacing */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                                    <div>
                                        <label className="text-gray-600 font-medium">Sticker Genişlik (cm)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="1"
                                            value={stickerW}
                                            onChange={e => setStickerW(parseFloat(e.target.value) || 1)}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-600 font-medium">Sticker Yükseklik (cm)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="1"
                                            value={stickerH}
                                            onChange={e => setStickerH(parseFloat(e.target.value) || 1)}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-600 font-medium">Kenar Boşluğu (cm)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={pageMargin}
                                            onChange={e => setPageMargin(parseFloat(e.target.value) || 0)}
                                            disabled={paperSize === 'Single'}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white disabled:opacity-50"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-600 font-medium">Sticker Aralığı (cm)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            min="0"
                                            value={gapX}
                                            onChange={e => {
                                                const v = parseFloat(e.target.value) || 0;
                                                setGapX(v);
                                                setGapY(v);
                                            }}
                                            disabled={paperSize === 'Single'}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white disabled:opacity-50"
                                        />
                                    </div>
                                </div>

                                {/* ── Enhanced Cut Guides & Labels Configuration Card ── */}
                                <div className="p-3.5 bg-white rounded-xl border border-amber-200 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-gray-800">
                                            <input
                                                type="checkbox"
                                                checked={showCutGuides}
                                                onChange={e => setShowCutGuides(e.target.checked)}
                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            />
                                            <span className="flex items-center gap-1.5">
                                                <FaCut className="text-amber-600 text-xs" />
                                                <span>Kesim Kılavuz Çizgileri (Crop Marks & Çerçeve)</span>
                                            </span>
                                        </label>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                                            showCutGuides ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                            {showCutGuides ? 'Aktif' : 'Kapalı'}
                                        </span>
                                    </div>

                                    {showCutGuides && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-amber-100 text-xs">
                                            <div>
                                                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                                    Kılavuz Çizgi Tipi
                                                </label>
                                                <select
                                                    value={cutGuideStyle}
                                                    onChange={e => setCutGuideStyle(e.target.value)}
                                                    className="w-full border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:ring-2 focus:ring-blue-500"
                                                >
                                                    <option value="crop_marks">Matbaa Köşe Kılavuzları (L-İşaretleri - Giyotin için ideal)</option>
                                                    <option value="dashed_border">Kesikli Çerçeve (Makas Kılavuzu)</option>
                                                    <option value="both">Her İkisi (Köşe + Kesikli Çerçeve)</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-[11px] font-semibold text-gray-600 mb-1">
                                                    Kılavuz Rengi
                                                </label>
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setCutGuideColor('dark')}
                                                        className={`flex-1 py-1 px-2 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1 transition-all ${
                                                            cutGuideColor === 'dark' ? 'bg-gray-800 text-white border-gray-800 shadow-xs' : 'bg-white text-gray-700 border-gray-300'
                                                        }`}
                                                    >
                                                        <span className="w-2 h-2 rounded-full bg-gray-800 border border-white" />
                                                        <span>Koyu Gri</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCutGuideColor('light')}
                                                        className={`flex-1 py-1 px-2 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1 transition-all ${
                                                            cutGuideColor === 'light' ? 'bg-gray-200 text-gray-900 border-gray-400 shadow-xs' : 'bg-white text-gray-700 border-gray-300'
                                                        }`}
                                                    >
                                                        <span className="w-2 h-2 rounded-full bg-gray-300 border border-gray-400" />
                                                        <span>Açık Gri</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setCutGuideColor('red')}
                                                        className={`flex-1 py-1 px-2 rounded-lg text-xs font-semibold border flex items-center justify-center gap-1 transition-all ${
                                                            cutGuideColor === 'red' ? 'bg-red-600 text-white border-red-600 shadow-xs' : 'bg-white text-gray-700 border-gray-300'
                                                        }`}
                                                    >
                                                        <span className="w-2 h-2 rounded-full bg-red-600" />
                                                        <span>Kırmızı</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="pt-2 border-t border-amber-100 flex items-center justify-between text-xs">
                                        <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
                                            <input
                                                type="checkbox"
                                                checked={showLabelCode}
                                                onChange={e => setShowLabelCode(e.target.checked)}
                                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                            />
                                            <span>QR Kod ID Metnini Yaz</span>
                                        </label>
                                    </div>
                                </div>

                                {/* Calculated Grid Notice */}
                                <div className="p-3 bg-white rounded-lg border border-amber-200 text-xs text-amber-900 flex items-start gap-2">
                                    <FaInfoCircle className="text-amber-600 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="font-bold">
                                            {paperSize} {orientation === 'landscape' ? 'Yatay' : 'Dikey'} Izgara Planı:
                                        </p>
                                        <p>
                                            Her sayfada <span className="font-bold">{gridCalc.cols} sütun × {gridCalc.rows} satır</span> olmak üzere toplam <span className="font-bold text-amber-700">{gridCalc.perPage} adet sticker</span> basılacak.
                                        </p>
                                        <p className="text-[11px] text-gray-500 mt-0.5">
                                            {totalSelectedCount} adet kod için yaklaşık <span className="font-bold text-gray-800">{totalSheetsEstimate} sayfa</span> PDF üretilecek.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* ── Section: Sticker Background Images ── */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Ön Yüz Görseli (İsteğe Bağlı)
                                    </label>
                                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                                        {stickerImageUrl ? (
                                            <img src={stickerImageUrl} alt="front" className="h-full w-full object-contain p-1" />
                                        ) : (
                                            <div className="text-center">
                                                <FaUpload className="text-gray-400 mx-auto text-lg mb-1" />
                                                <span className="text-[11px] text-gray-500">Görsel Seç</span>
                                            </div>
                                        )}
                                        <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                                    </label>
                                    {stickerImageUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setStickerImageUrl(null)}
                                            className="mt-1 text-[11px] text-red-500 hover:underline"
                                        >
                                            Kaldır
                                        </button>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                                        Arka Yüz Görseli (İsteğe Bağlı)
                                    </label>
                                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors">
                                        {backsideImageUrl ? (
                                            <img src={backsideImageUrl} alt="back" className="h-full w-full object-contain p-1" />
                                        ) : (
                                            <div className="text-center">
                                                <FaUpload className="text-gray-400 mx-auto text-lg mb-1" />
                                                <span className="text-[11px] text-gray-500">Görsel Seç</span>
                                            </div>
                                        )}
                                        <input type="file" accept="image/*" className="hidden" onChange={handleBacksideUpload} />
                                    </label>
                                    {backsideImageUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setBacksideImageUrl(null)}
                                            className="mt-1 text-[11px] text-red-500 hover:underline"
                                        >
                                            Kaldır
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* ── Section: QR Code Position & Styling on Sticker ── */}
                            <div className="p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-3">
                                <span className="text-xs font-bold text-gray-800 block">
                                    QR Kod Konumu & Stili
                                </span>
                                <div className="grid grid-cols-3 gap-3 text-xs">
                                    <div>
                                        <label className="text-gray-600 font-medium">Sol (X) cm</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={qrX}
                                            onChange={e => setQrX(parseFloat(e.target.value) || 0)}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-600 font-medium">Üst (Y) cm</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={qrY}
                                            onChange={e => setQrY(parseFloat(e.target.value) || 0)}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-gray-600 font-medium">QR Boyutu (cm)</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={qrSize}
                                            onChange={e => setQrSize(parseFloat(e.target.value) || 1)}
                                            className="w-full mt-1 border rounded-lg px-2.5 py-1.5 bg-white"
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between pt-2 border-t text-xs">
                                    <div>
                                        <span className="text-gray-800 font-semibold block">Şeffaf Arka Plan</span>
                                        <span className="text-[11px] text-gray-500">QR kodu altındaki beyaz zemin kutusunu kaldırır</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setQrTransparent(v => !v)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                                            qrTransparent ? 'bg-blue-600 text-white shadow-xs' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                        }`}
                                    >
                                        {qrTransparent ? 'Şeffaf (Aktif)' : 'Beyaz Zemin'}
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t text-xs">
                                    <div>
                                        <div className="flex justify-between items-center text-gray-700 font-medium mb-1">
                                            <span>Piksel / Nokta Yuvarlaklığı</span>
                                            <span className="font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[11px] font-bold">
                                                %{qrCornerRadius}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            step="1"
                                            value={qrCornerRadius}
                                            onChange={e => setQrCornerRadius(Number(e.target.value))}
                                            className="w-full accent-blue-600 cursor-pointer"
                                        />
                                        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                            <span>0% (Kare)</span>
                                            <span>50% (Yuvarlak Nokta)</span>
                                        </div>
                                    </div>

                                    <div>
                                        <div className="flex justify-between items-center text-gray-700 font-medium mb-1">
                                            <span>QR Genel Yuvarlaklığı (Dış Kutu)</span>
                                            <span className="font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[11px] font-bold">
                                                %{qrOuterRadius}
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="50"
                                            step="1"
                                            value={qrOuterRadius}
                                            onChange={e => setQrOuterRadius(Number(e.target.value))}
                                            className="w-full accent-blue-600 cursor-pointer"
                                        />
                                        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                                            <span>0% (Köşeli Kutu)</span>
                                            <span>50% (Tam Oval / Daire)</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Messages */}
                            {error && <p className="bg-red-50 text-red-700 p-3 rounded-xl text-xs font-medium border border-red-200">{error}</p>}
                            {success && <p className="bg-green-50 text-green-700 p-3 rounded-xl text-xs font-medium border border-green-200">{success}</p>}

                            {/* Progress bar */}
                            {loading && (
                                <div className="space-y-1">
                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                        <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                                    </div>
                                    <p className="text-xs text-gray-500 text-center font-mono">%{progress} Hazırlanıyor...</p>
                                </div>
                            )}

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={loading || totalSelectedCount === 0}
                                className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-xl text-sm shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                {loading ? (
                                    <>
                                        <FaSpinner className="animate-spin" />
                                        <span>PDF Oluşturuluyor...</span>
                                    </>
                                ) : (
                                    <>
                                        <FaPrint />
                                        <span>
                                            {totalSelectedCount} Adet QR ile {paperSize} PDF Tabakası Oluştur & İndir
                                        </span>
                                    </>
                                )}
                            </button>
                        </form>
                    </div>

                    {/* Right Previews: 5 cols */}
                    <div className="lg:col-span-5 space-y-6">

                        {/* ── Realistic Sheet Grid Visualizer Preview ── */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                            <div className="flex items-center justify-between border-b pb-3">
                                <div>
                                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                        <FaTh className="text-blue-600" />
                                        <span>Tabaka / Sayfa Izgara Önizlemesi</span>
                                    </h3>
                                    <p className="text-[11px] text-gray-500">
                                        {gridCalc.cols} sütun × {gridCalc.rows} satır ({gridCalc.perPage} adet/sayfa)
                                    </p>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    <span className="text-xs font-mono font-bold text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                                        {paperSize}
                                    </span>
                                    {showCutGuides ? (
                                        <span className="text-[10px] bg-amber-100 text-amber-800 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1">
                                            <FaCut size={9} />
                                            {cutGuideStyle === 'crop_marks' ? 'Köşe Kılavuzu' : cutGuideStyle === 'dashed_border' ? 'Kesikli Çizgi' : 'Köşe+Çerçeve'}
                                        </span>
                                    ) : (
                                        <span className="text-[10px] bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">
                                            Kılavuzsuz
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Scaled Proportional Paper Display via SVG */}
                            <div className="flex justify-center p-4 bg-slate-100 rounded-xl overflow-hidden">
                                <div
                                    className="bg-white shadow-lg border border-slate-300 relative rounded transition-all flex flex-col justify-center items-center p-2"
                                    style={{
                                        maxWidth: '100%',
                                        width: orientation === 'landscape' ? '320px' : '230px',
                                    }}
                                >
                                    <svg
                                        viewBox={`0 0 ${gridCalc.pageW} ${gridCalc.pageH}`}
                                        className="w-full h-auto drop-shadow-xs"
                                        style={{ maxHeight: '340px' }}
                                    >
                                        {/* White Paper Sheet Background */}
                                        <rect
                                            x="0"
                                            y="0"
                                            width={gridCalc.pageW}
                                            height={gridCalc.pageH}
                                            fill="#ffffff"
                                            stroke="#cbd5e1"
                                            strokeWidth="0.06"
                                            rx="0.1"
                                        />

                                        {/* Printable Margin Guideline (dashed) */}
                                        {pageMargin > 0 && (
                                            <rect
                                                x={pageMargin}
                                                y={pageMargin}
                                                width={Math.max(0, gridCalc.pageW - 2 * pageMargin)}
                                                height={Math.max(0, gridCalc.pageH - 2 * pageMargin)}
                                                fill="none"
                                                stroke="#e2e8f0"
                                                strokeWidth="0.04"
                                                strokeDasharray="0.3, 0.2"
                                            />
                                        )}

                                        {/* Render Each Sticker on the Page */}
                                        {sheetStickers.map((s) => {
                                            const isFirstCol = s.c === 0;
                                            const isLastCol = s.c === gridCalc.cols - 1;
                                            const isFirstRow = s.r === 0;
                                            const isLastRow = s.r === gridCalc.rows - 1;

                                            const baseMark = 0.25;
                                            const leftLen = isFirstCol ? Math.min(baseMark, pageMargin * 0.8) : (gapX > 0 ? Math.min(baseMark, (gapX / 2) * 0.85) : 0);
                                            const rightLen = isLastCol ? Math.min(baseMark, pageMargin * 0.8) : (gapX > 0 ? Math.min(baseMark, (gapX / 2) * 0.85) : 0);
                                            const topLen = isFirstRow ? Math.min(baseMark, pageMargin * 0.8) : (gapY > 0 ? Math.min(baseMark, (gapY / 2) * 0.85) : 0);
                                            const botLen = isLastRow ? Math.min(baseMark, pageMargin * 0.8) : (gapY > 0 ? Math.min(baseMark, (gapY / 2) * 0.85) : 0);

                                            const miniOuterR = qrOuterRadius > 0 ? (qrSize / 2) * (qrOuterRadius / 50) : 0;

                                            return (
                                                <g key={s.idx}>
                                                    {/* Sticker Background */}
                                                    <rect
                                                        x={s.x}
                                                        y={s.y}
                                                        width={stickerW}
                                                        height={stickerH}
                                                        fill="#f8fafc"
                                                        stroke="#bfdbfe"
                                                        strokeWidth="0.03"
                                                        rx="0.05"
                                                    />

                                                    {/* Optional miniature image */}
                                                    {stickerImageUrl && (
                                                        <image
                                                            href={stickerImageUrl}
                                                            x={s.x}
                                                            y={s.y}
                                                            width={stickerW}
                                                            height={stickerH}
                                                            preserveAspectRatio="none"
                                                        />
                                                    )}

                                                    {/* Miniature QR representation with realistic outer radius */}
                                                    <rect
                                                        x={s.x + qrX}
                                                        y={s.y + qrY}
                                                        width={qrSize}
                                                        height={qrSize}
                                                        rx={miniOuterR}
                                                        fill={qrTransparent ? 'none' : '#ffffff'}
                                                        stroke="#1e293b"
                                                        strokeWidth="0.04"
                                                    />
                                                    <rect
                                                        x={s.x + qrX + qrSize * 0.25}
                                                        y={s.y + qrY + qrSize * 0.25}
                                                        width={qrSize * 0.5}
                                                        height={qrSize * 0.5}
                                                        rx={miniOuterR * 0.5}
                                                        fill="#0f172a"
                                                    />

                                                    {/* Sticker number badge */}
                                                    <text
                                                        x={s.x + stickerW / 2}
                                                        y={s.y + stickerH - (showLabelCode ? 0.22 : 0.12)}
                                                        fontSize={Math.min(0.32, stickerH * 0.1)}
                                                        textAnchor="middle"
                                                        fill="#1e40af"
                                                        fontWeight="bold"
                                                        style={{ userSelect: 'none' }}
                                                    >
                                                        #{s.idx}
                                                    </text>

                                                    {/* Cut guides on SVG */}
                                                    {showCutGuides && (
                                                        <g>
                                                            {/* Dashed border if style is dashed_border or both */}
                                                            {(cutGuideStyle === 'dashed_border' || cutGuideStyle === 'both') && (
                                                                <rect
                                                                    x={s.x}
                                                                    y={s.y}
                                                                    width={stickerW}
                                                                    height={stickerH}
                                                                    fill="none"
                                                                    stroke={svgCutColor}
                                                                    strokeWidth="0.04"
                                                                    strokeDasharray="0.15, 0.12"
                                                                />
                                                            )}

                                                            {/* Corner crop marks if style is crop_marks or both */}
                                                            {(cutGuideStyle === 'crop_marks' || cutGuideStyle === 'both') && (
                                                                <g stroke={svgCutColor} strokeWidth="0.035" strokeLinecap="round">
                                                                    {/* Top-Left */}
                                                                    {leftLen > 0 && <line x1={s.x - leftLen} y1={s.y} x2={s.x} y2={s.y} />}
                                                                    {topLen > 0 && <line x1={s.x} y1={s.y - topLen} x2={s.x} y2={s.y} />}

                                                                    {/* Top-Right */}
                                                                    {rightLen > 0 && <line x1={s.x + stickerW} y1={s.y} x2={s.x + stickerW + rightLen} y2={s.y} />}
                                                                    {topLen > 0 && <line x1={s.x + stickerW} y1={s.y - topLen} x2={s.x + stickerW} y2={s.y} />}

                                                                    {/* Bottom-Left */}
                                                                    {leftLen > 0 && <line x1={s.x - leftLen} y1={s.y + stickerH} x2={s.x} y2={s.y + stickerH} />}
                                                                    {botLen > 0 && <line x1={s.x} y1={s.y + stickerH} x2={s.x} y2={s.y + stickerH + botLen} />}

                                                                    {/* Bottom-Right */}
                                                                    {rightLen > 0 && <line x1={s.x + stickerW} y1={s.y + stickerH} x2={s.x + stickerW + rightLen} y2={s.y + stickerH} />}
                                                                    {botLen > 0 && <line x1={s.x + stickerW} y1={s.y + stickerH} x2={s.x + stickerW + rightLen} y2={s.y + stickerH + botLen} />}
                                                                </g>
                                                            )}
                                                        </g>
                                                    )}
                                                </g>
                                            );
                                        })}
                                    </svg>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                                <span>Sayfa: {gridCalc.pageW} × {gridCalc.pageH} cm</span>
                                <span>Aralık: {gapX} cm | Kenar: {pageMargin} cm</span>
                            </div>
                        </div>

                        {/* ── Individual Sticker Detail Preview ── */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
                            <div className="flex items-center justify-between border-b pb-3">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                                        <FaEye className="text-indigo-600" />
                                        <span>Tekil Sticker Detayı</span>
                                    </h3>
                                    {showCutGuides && (
                                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200">
                                            Kesim Kılavuzlu
                                        </span>
                                    )}
                                </div>
                                {backsideImageUrl && (
                                    <div className="flex rounded-lg overflow-hidden border border-gray-200 text-xs">
                                        <button
                                            type="button"
                                            onClick={() => setPreviewSide('front')}
                                            className={`px-3 py-1 font-semibold ${previewSide === 'front' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                                        >
                                            Ön
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPreviewSide('back')}
                                            className={`px-3 py-1 font-semibold ${previewSide === 'back' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600'}`}
                                        >
                                            Arka
                                        </button>
                                    </div>
                                )}
                            </div>

                            <div className="flex justify-center p-6 bg-slate-100 rounded-xl overflow-hidden relative">
                                {/* Outer padding container so crop marks outside the sticker boundary are visible */}
                                <div className="relative p-4">
                                    {/* The Sticker Frame */}
                                    <div
                                        className="relative bg-white shadow-md border border-gray-300 rounded overflow-hidden"
                                        style={{
                                            width: `${stickerW * previewScale}px`,
                                            height: `${stickerH * previewScale}px`,
                                        }}
                                    >
                                        {previewSide === 'front' && stickerImageUrl && (
                                            <img src={stickerImageUrl} alt="front-bg" className="absolute inset-0 w-full h-full object-fill" />
                                        )}
                                        {previewSide === 'back' && backsideImageUrl && (
                                            <img src={backsideImageUrl} alt="back-bg" className="absolute inset-0 w-full h-full object-fill" />
                                        )}

                                        {/* QR Canvas Placement */}
                                        {previewSide === 'front' && (
                                            <div
                                                className="absolute transition-all"
                                                style={{
                                                    left: `${qrX * previewScale}px`,
                                                    top: `${qrY * previewScale}px`,
                                                    width: `${qrSize * previewScale}px`,
                                                    height: `${qrSize * previewScale}px`,
                                                    borderRadius: `${previewOuterRPx}px`,
                                                    overflow: 'hidden',
                                                    backgroundColor: qrTransparent ? 'transparent' : '#ffffff',
                                                    boxShadow: !qrTransparent && !stickerImageUrl ? '0 1px 4px rgba(0,0,0,0.1)' : undefined,
                                                    border: !qrTransparent && !stickerImageUrl ? '1px solid #e2e8f0' : undefined
                                                }}
                                            >
                                                <canvas
                                                    ref={qrPreviewCanvasRef}
                                                    className="w-full h-full object-contain"
                                                    style={{
                                                        borderRadius: `${previewOuterRPx}px`
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {/* Dashed cut line on sticker border if requested */}
                                        {showCutGuides && (cutGuideStyle === 'dashed_border' || cutGuideStyle === 'both') && (
                                            <div
                                                className="absolute inset-0 pointer-events-none border-2 border-dashed z-10"
                                                style={{
                                                    borderColor: svgCutColor,
                                                    opacity: 0.8
                                                }}
                                            />
                                        )}
                                    </div>

                                    {/* Corner Crop Marks around the sticker if requested */}
                                    {showCutGuides && (cutGuideStyle === 'crop_marks' || cutGuideStyle === 'both') && (
                                        <div className="absolute inset-4 pointer-events-none z-20">
                                            {/* Top-Left */}
                                            <div className="absolute -top-3.5 left-0 w-0.5 h-3" style={{ backgroundColor: svgCutColor }} />
                                            <div className="absolute top-0 -left-3.5 w-3 h-0.5" style={{ backgroundColor: svgCutColor }} />

                                            {/* Top-Right */}
                                            <div className="absolute -top-3.5 right-0 w-0.5 h-3" style={{ backgroundColor: svgCutColor }} />
                                            <div className="absolute top-0 -right-3.5 w-3 h-0.5" style={{ backgroundColor: svgCutColor }} />

                                            {/* Bottom-Left */}
                                            <div className="absolute -bottom-3.5 left-0 w-0.5 h-3" style={{ backgroundColor: svgCutColor }} />
                                            <div className="absolute bottom-0 -left-3.5 w-3 h-0.5" style={{ backgroundColor: svgCutColor }} />

                                            {/* Bottom-Right */}
                                            <div className="absolute -bottom-3.5 right-0 w-0.5 h-3" style={{ backgroundColor: svgCutColor }} />
                                            <div className="absolute bottom-0 -right-3.5 w-3 h-0.5" style={{ backgroundColor: svgCutColor }} />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Safe zone check tip */}
                            {(qrX < 0.2 || qrY < 0.2 || (qrX + qrSize > stickerW - 0.2) || (qrY + qrSize > stickerH - 0.2)) && (
                                <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200 text-[11px] text-amber-800 flex items-start gap-1.5">
                                    <FaInfoCircle className="text-amber-600 mt-0.5 flex-shrink-0" />
                                    <span>
                                        <strong>Güvenli Alan Uyarısı:</strong> QR kodu kesim kenarına çok yakın (&lt;2mm). Kesim sırasında QR kodunun zarar görmemesi için kenarlardan en az 0.3 cm pay bırakılması önerilir.
                                    </span>
                                </div>
                            )}

                            <div className="flex items-center justify-between text-xs text-gray-500 font-mono px-1">
                                <span>Boyut: {stickerW} × {stickerH} cm</span>
                                <span>QR: {qrSize} cm (Yuvarlaklık: %{qrOuterRadius})</span>
                            </div>
                        </div>

                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminPrintQrPage;
